/**
 * Auth user/session/account persistence schema.
 *
 * Invariants:
 * - `user.status` models waitlist lifecycle (`pending` until invited activation).
 * - Session/account rows are owned by a user and cascade on user deletion.
 *
 * @see `packages/core/docs/database.md`
 */
import { pgTable, text, boolean, uuid, pgEnum, timestamp } from "drizzle-orm/pg-core";

const authTimestamp = (name: string) =>
  timestamp(name, {
    withTimezone: true,
    mode: "date",
    precision: 0,
  });

export const userStatusEnum = pgEnum("user_status", ["pending", "active"]);

export const user = pgTable("user", {
  id: uuid("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").notNull(),
  image: text("image"),
  createdAt: authTimestamp("created_at").notNull(),
  updatedAt: authTimestamp("updated_at").notNull(),
  role: text("role"),
  banned: boolean("banned"),
  banReason: text("ban_reason"),
  banExpires: authTimestamp("ban_expires"),
  status: userStatusEnum("status").notNull().default("pending"),
});

export const session = pgTable("session", {
  id: uuid("id").primaryKey(),
  expiresAt: authTimestamp("expires_at").notNull(),
  token: text("token").notNull().unique(),
  createdAt: authTimestamp("created_at").notNull(),
  updatedAt: authTimestamp("updated_at").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: uuid("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  impersonatedBy: uuid("impersonated_by"),
});

export const account = pgTable("account", {
  id: uuid("id").primaryKey(),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: uuid("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: authTimestamp("access_token_expires_at"),
  refreshTokenExpiresAt: authTimestamp("refresh_token_expires_at"),
  scope: text("scope"),
  password: text("password"),
  createdAt: authTimestamp("created_at").notNull(),
  updatedAt: authTimestamp("updated_at").notNull(),
});

export const verification = pgTable("verification", {
  id: uuid("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: authTimestamp("expires_at").notNull(),
  createdAt: authTimestamp("created_at"),
  updatedAt: authTimestamp("updated_at"),
});
